/**
 * Pivot table types, grouping engine, and CSV row shaping.
 * This file exists to break circular dependencies between pivot-table.tsx and
 * pivot-table-export.ts.
 */

import type React from 'react';
import {
  chain,
  get,
  groupBy,
  identity,
  isNumber,
  startCase,
  sumBy,
} from 'lodash';
import {
  type ExtractReturns,
  type Prettify,
} from '@bbkAdminUtils/utility-types';
import {
  dataToCsvString,
  type CsvExportField,
} from '@bbkShared/utils/export-file';

export namespace PivotT {
  export type DataType = Record<string, unknown>;

  export type FnAny = (...args: any[]) => any;

  export type PivotTableGroupObject<T, K extends keyof T = keyof T> = {
    key: K;
    value: T[K];
  };
  export type WithInternalPivotFields<T, K extends keyof T = keyof T> = T & {
    __PIVOT_TABLE: {
      GROUP_KEY: string;
      GROUP_OBJECT: PivotTableGroupObject<T, K>[];
    };
  };

  export type CalculatedFields<T extends DataType> = {
    (row: T): Record<string, () => unknown>;
  };

  export type CalculatedFieldsLoose<T extends DataType> = {
    (row: any): Record<string, () => any>;
  };

  export type ComputedFieldsOnly<
    T extends DataType,
    CF extends CalculatedFields<T>,
  > = Prettify<ExtractReturns<ReturnType<CF>>>;

  export type ComputedFields<
    T extends DataType,
    CF extends CalculatedFields<T>,
  > = Prettify<T & ComputedFieldsOnly<T, CF>>;

  export type PivotValue<T extends DataType> = {
    field: keyof T;
    label: string;
    format: (val: number) => string;
    tooltip?: (row: T) => React.ReactNode;
  };

  export type GroupField<T extends DataType> = {
    field: Array<keyof T>;
    label: string;
    name: string;
    icon: React.ReactNode;
    tooltip?: (row: any) => React.ReactNode;
  };

  export type PivotValueLoose<T extends DataType> = {
    field: string;
    label: string;
    format: (val: number) => string;
    tooltip?: (row: any) => React.ReactNode;
  };

  export type GroupFieldLoose<T extends DataType> = {
    field: Array<string>;
    label: string;
    name: string;
    icon: React.ReactNode;
    tooltip?: (row: any) => React.ReactNode;
  };
}

export class PivotTable<
  T extends PivotT.DataType,
  CF extends PivotT.CalculatedFields<T> = PivotT.CalculatedFields<T>,
  PR extends PivotT.ComputedFields<T, CF> = PivotT.ComputedFields<T, CF>,
> {
  public static GROUP_KEY_SEPARATOR = '-[|||]-';

  private calculatedFields: CF;

  constructor(calculatedFields: CF) {
    this.calculatedFields = calculatedFields;
  }

  public calculateRow = <TT extends T>(
    row: TT
  ): PivotT.ComputedFields<TT, CF> => {
    const newFields = Object.fromEntries(
      Object.entries(this.calculatedFields(row)).map(([key, fn]) => {
        const val = fn();
        return [key, Number.isNaN(val) ? 0 : val];
      })
    ) as PivotT.ComputedFieldsOnly<TT, CF>;
    return {
      ...row,
      ...newFields,
    };
  };

  public getGroupKey = (x: PR, fields: string[]): string =>
    fields.map((field) => get(x, field)).join(PivotTable.GROUP_KEY_SEPARATOR);

  private getGroupObject = (
    x: PR,
    fields: string[]
  ): PivotT.PivotTableGroupObject<PR>[] =>
    fields.map((field) => ({ key: field, value: get(x, field) }));

  private rollup = (values: PR[]): PR => {
    const firstRow = values[0];
    const keys = Object.keys(firstRow ?? []);
    return Object.fromEntries(
      keys.map((k) => [
        k,
        isNumber(get(firstRow, k)) ? sumBy(values, k) : get(firstRow, k),
      ])
    ) as PR;
  };

  public group = (
    values: PR[],
    fields: string[] = []
  ): PivotT.WithInternalPivotFields<PR>[] => {
    const grouped = groupBy(values, (x) => this.getGroupKey(x, fields));
    return Object.entries(grouped)
      .map(([GROUP_KEY, arr]) => ({
        ...this.rollup(arr),
        __PIVOT_TABLE: {
          GROUP_KEY,
          GROUP_OBJECT: this.getGroupObject(arr[0]!, fields),
        },
      }))
      .map((el) => this.calculateRow(el));
  };
}

type BuildPivotTableExportRowsArgs<
  T extends PivotT.DataType,
  CF extends PivotT.CalculatedFieldsLoose<T>,
> = {
  data: T[];
  calculatedFields: CF;
  pivotValues: PivotT.PivotValueLoose<PivotT.ComputedFields<T, CF>>[];
  pivotRows: PivotT.GroupFieldLoose<PivotT.ComputedFields<T, CF>>[];
  visibleColumns: string[];
  sortField?: string;
  sortOrderDesc?: boolean;
};

export const buildPivotTableExportRows = <
  T extends PivotT.DataType,
  CF extends PivotT.CalculatedFieldsLoose<T>,
>({
  data,
  calculatedFields,
  pivotValues,
  pivotRows,
  visibleColumns,
  sortField,
  sortOrderDesc = true,
}: BuildPivotTableExportRowsArgs<T, CF>): {
  fields: CsvExportField[];
  rows: Record<string, unknown>[];
} => {
  const pivotTable = new PivotTable(calculatedFields);
  const resolvedSortField = sortField ?? String(pivotValues[0]?.field ?? '');

  const fields: CsvExportField[] = [
    ...pivotRows.flatMap((pr) => pr.field),
    ...visibleColumns,
  ].map((column) => {
    const pivotValue = pivotValues.find((pv) => pv.field === column);
    if (!pivotValue) {
      return { value: column, label: startCase(column) };
    }
    return { value: String(pivotValue.field), label: pivotValue.label };
  });

  const groupFields = pivotRows.flatMap((row) => row.field) as string[];

  const rows = chain(
    pivotTable.group(
      data.map((row) => pivotTable.calculateRow(row)),
      groupFields
    )
  )
    .map((row) =>
      Object.fromEntries(
        Object.entries(row)
          .filter(([key]) => key !== '__PIVOT_TABLE')
          .map(([key, value]) => {
            const pivotValue = pivotValues.find((pv) => pv.field === key);
            const formattedValue = (pivotValue?.format ?? identity)(
              value as number
            );
            return [key, formattedValue];
          })
      )
    )
    .orderBy(
      [...groupFields, resolvedSortField],
      [...groupFields.map(() => 'asc' as const), sortOrderDesc ? 'desc' : 'asc']
    )
    .value();

  return { fields, rows };
};

export const exportPivotTableToCsvString = <
  T extends PivotT.DataType,
  CF extends PivotT.CalculatedFieldsLoose<T>,
>(
  args: BuildPivotTableExportRowsArgs<T, CF>
): string => {
  const { fields, rows } = buildPivotTableExportRows(args);
  return dataToCsvString(rows, fields);
};
